import React from 'react';

import type { Nullable } from '@clearverify/types';

import {
  CurrentUserDocument,
  UserAvatarDocument,
  useUpdateUserMutation,
  useUserAvatarQuery,
} from 'src/api';
import { DroppedFile } from 'src/types/file';

import { useNotification } from '../app';
import { useFileUpload } from '../file';

export const useUserAvatar = (userId: Nullable<string>) => {
  const { createApolloContext } = useNotification();

  const { uploadFile, uploading } = useFileUpload();

  const [userUpdateMutation, { loading: userAvatarUpdating }] = useUpdateUserMutation({
    awaitRefetchQueries: true,
    refetchQueries: [UserAvatarDocument, CurrentUserDocument],
    context: createApolloContext({
      errorMessage: 'Something went wrong while updating avatar',
    }),
  });

  const { data, loading: userAvatarLoading } = useUserAvatarQuery({
    variables: {
      id: userId || '',
    },
    skip: !userId,
  });

  const user = data?.user;
  const userAvatar = user?.avatar;

  const updateUserAvatar = React.useCallback(
    async (logoFile: DroppedFile | string) => {
      // TODO: handle case with fileId
      if (typeof logoFile === 'string') throw new Error('Not implemented');
      if (!logoFile.file) return;

      try {
        const uploadedFile = await uploadFile(logoFile.file);
        await userUpdateMutation({
          variables: {
            where: { id: userId },
            data: {
              avatar: {
                connect: { id: uploadedFile.id },
              },
            },
          },
        });
      } catch (error) {
        console.error(error);
      }
    },
    [userId, userUpdateMutation, uploadFile],
  );

  return {
    userAvatar,
    userAvatarLoading,
    updateUserAvatar,
    userAvatarUpdating: uploading || userAvatarUpdating,
  };
};
